import { useEffect, useState } from "react";
import {
  Flask,
  Snowflake,
  ChartLineUp,
  Sparkle,
  Trophy,
  ArrowClockwise,
  Info,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { fmtMoney } from "../../lib/finance-format";

/**
 * What-If Lab — interactive slider that shows how extra monthly payments
 * shorten the debt-free timeline and reduce total interest paid.
 * Backend: POST /api/finance/debt-plan (avalanche vs snowball).
 */
export default function WhatIfLab({ scope }) {
  const [extra, setExtra] = useState(200);
  const [strategy, setStrategy] = useState("avalanche"); // avalanche | snowball
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (amount) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await api.post("/finance/debt-plan", {
        scope,
        strategy,
        extra_monthly: amount,
      });
      setData(res);
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo calcular");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Debounced load on slider change
  useEffect(() => {
    const t = setTimeout(() => load(extra), 350);
    return () => clearTimeout(t);
  }, [extra, strategy, scope]);

  const chosen = data?.chosen || null;
  const baseline = data?.baseline_no_extra || null;
  const empty = data?.empty;

  const months = chosen?.months_to_freedom ?? null;
  const totalInterest = chosen?.total_interest ?? null;
  const baselineMonths = baseline?.months_to_freedom ?? null;
  const baselineInterest = baseline?.total_interest ?? null;
  const savings = data?.savings_vs_baseline || {};
  const savedMonths = savings.months_saved ?? 0;
  const savedInterest = savings.interest_saved ?? 0;
  const savedPct = baselineInterest > 0
    ? Math.round((savedInterest / baselineInterest) * 100)
    : 0;

  // Build ordered debt list for the "orden de pago" table from top-level debts + chosen.order
  const orderedDebts = chosen?.order?.length && data?.debts?.length
    ? chosen.order
        .map((name) => data.debts.find((d) => d.name === name))
        .filter(Boolean)
    : [];

  const yearsMonthsText = (n) => {
    if (n == null) return "—";
    const y = Math.floor(n / 12);
    const m = n % 12;
    if (y === 0) return `${m} mes${m === 1 ? "" : "es"}`;
    if (m === 0) return `${y} año${y === 1 ? "" : "s"}`;
    return `${y}a ${m}m`;
  };

  return (
    <section
      data-testid="whatif-lab"
      className="rounded-lg border border-white/8 bg-black/20 p-4 md:p-5"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#8B5CF6] flex items-center gap-1.5">
            <Flask size={12} weight="fill" /> WHAT-IF LAB · SIMULADOR
          </div>
          <h3 className="text-base md:text-lg font-heading font-bold text-white">
            ¿Y si pago más cada mes?
          </h3>
        </div>
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-black/40 border border-white/8">
          <button
            data-testid="whatif-strategy-avalanche"
            onClick={() => setStrategy("avalanche")}
            className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded transition ${
              strategy === "avalanche"
                ? "on-solid bg-[#6D28D9] text-white"
                : "text-white/45 hover:text-white/80"
            }`}
          >
            <ChartLineUp size={10} weight="bold" className="inline mr-1" />
            Avalancha
          </button>
          <button
            data-testid="whatif-strategy-snowball"
            onClick={() => setStrategy("snowball")}
            className={`px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold rounded transition ${
              strategy === "snowball"
                ? "on-solid bg-emerald-700 text-white"
                : "text-white/45 hover:text-white/80"
            }`}
          >
            <Snowflake size={10} weight="bold" className="inline mr-1" />
            Bola de nieve
          </button>
        </div>
      </div>

      {empty ? (
        <div className="py-12 text-center text-xs font-mono text-white/40">
          <Sparkle size={20} weight="fill" className="text-[#8B5CF6] mx-auto mb-2" />
          Añade una deuda para usar el simulador.
        </div>
      ) : (
        <>
          {/* Slider */}
          <div className="rounded-lg bg-black/30 border border-white/8 p-4 mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">
                Extra mensual sobre pagos mínimos
              </div>
              <div className="font-mono font-bold tabular-nums text-2xl text-[#FACC15]">
                +${extra.toLocaleString()}
                <span className="text-xs text-white/50">/mes</span>
              </div>
            </div>
            <input
              data-testid="whatif-slider"
              type="range"
              min={0}
              max={2000}
              step={25}
              value={extra}
              onChange={(e) => setExtra(parseInt(e.target.value, 10))}
              className="w-full accent-[#FACC15]"
            />
            <div className="flex items-center justify-between text-[9px] font-mono text-white/30 mt-1">
              <span>$0</span>
              <span>$500</span>
              <span>$1,000</span>
              <span>$1,500</span>
              <span>$2,000+</span>
            </div>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {[0, 100, 200, 500, 1000, 1500].map((v) => (
                <button
                  key={v}
                  data-testid={`whatif-preset-${v}`}
                  onClick={() => setExtra(v)}
                  className={`px-2.5 py-1 rounded text-[10px] font-mono uppercase tracking-widest font-bold transition ${
                    extra === v
                      ? "bg-white/15 text-white"
                      : "bg-white/5 text-white/50 hover:text-white/80"
                  }`}
                >
                  {v === 0 ? "Sin extra" : `+${v.toString().replace(/(\d)(?=(\d{3})+$)/g, "$1,")}`}
                </button>
              ))}
            </div>
          </div>

          {/* Result cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <ResultCard
              testId="whatif-months"
              accent="#8B5CF6"
              label="Meses hasta libertad"
              value={yearsMonthsText(months)}
              hint={baselineMonths ? `Base: ${yearsMonthsText(baselineMonths)}` : null}
              loading={loading}
            />
            <ResultCard
              testId="whatif-months-saved"
              accent="#10B981"
              label="Meses ahorrados"
              value={savedMonths > 0 ? yearsMonthsText(savedMonths) : "—"}
              hint={savedMonths > 0 ? "Terminas antes vs. mínimo" : "Aumenta el extra"}
              loading={loading}
              highlight={savedMonths > 0}
            />
            <ResultCard
              testId="whatif-interest"
              accent="#F97316"
              label="Interés total"
              value={totalInterest != null ? fmtMoney(totalInterest) : "—"}
              hint={baselineInterest ? `Base: ${fmtMoney(baselineInterest)}` : null}
              loading={loading}
            />
            <ResultCard
              testId="whatif-interest-saved"
              accent="#FACC15"
              label="Interés ahorrado"
              value={savedInterest > 0 ? fmtMoney(savedInterest) : "—"}
              hint={savedPct > 0 ? `${savedPct}% menos` : null}
              loading={loading}
              highlight={savedInterest > 0}
            />
          </div>

          {/* Insight */}
          {savedInterest > 0 && (
            <div className="mt-4 rounded-lg bg-[#8B5CF6]/10 border border-[#8B5CF6]/25 p-3 flex gap-2 items-start">
              <Info size={13} weight="fill" className="text-[#DDD6FE] shrink-0 mt-0.5" />
              <div className="text-[11px] font-mono text-white/70 leading-relaxed">
                Con <b className="text-white">+${extra}/mes</b> extra, terminas de pagar tus deudas{" "}
                <b className="text-emerald-300">{yearsMonthsText(savedMonths)}</b> antes y ahorras{" "}
                <b className="text-[#FACC15]">{fmtMoney(savedInterest)}</b> en intereses ({savedPct}% menos).
                {strategy === "avalanche"
                  ? " Estrategia avalancha: matas primero la deuda con mayor APR."
                  : " Estrategia bola de nieve: matas primero la deuda más pequeña para ganar momento."}
              </div>
            </div>
          )}

          {/* Payoff schedule (order) */}
          {orderedDebts.length > 0 && (
            <div className="mt-4 rounded-lg border border-white/6 overflow-hidden">
              <div className="px-3 py-2 bg-black/40 border-b border-white/6 text-[10px] font-mono uppercase tracking-widest text-white/40 flex items-center gap-1.5">
                <Trophy size={11} weight="fill" className="text-[#FACC15]" />
                ORDEN DE PAGO — {strategy === "avalanche" ? "MAYOR APR PRIMERO" : "MENOR SALDO PRIMERO"}
              </div>
              <div className="divide-y divide-white/6">
                {orderedDebts.map((d, i) => (
                  <div
                    key={i}
                    data-testid={`whatif-debt-${i}`}
                    className="grid grid-cols-12 gap-2 px-3 py-2 items-center text-[11px] font-mono"
                  >
                    <div className="col-span-1 text-white/40 tabular-nums text-right">
                      #{i + 1}
                    </div>
                    <div className="col-span-5 text-white truncate font-heading font-semibold">
                      {d.name}
                    </div>
                    <div className="col-span-3 text-white/60">
                      APR {d.apr?.toFixed(2)}%
                    </div>
                    <div className="col-span-3 text-right text-white/60 tabular-nums">
                      {fmtMoney(d.balance)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="mt-3 rounded-lg bg-red-500/10 border border-red-500/25 p-2 text-[11px] font-mono text-red-300">
          {error}
        </div>
      )}
    </section>
  );
}

function ResultCard({ testId, accent, label, value, hint, loading, highlight }) {
  return (
    <div
      data-testid={testId}
      className="rounded-lg bg-black/30 border p-3 relative overflow-hidden"
      style={{
        borderColor: highlight ? accent + "88" : "rgba(255,255,255,0.08)",
      }}
    >
      <div
        className="absolute left-0 top-0 bottom-0 w-1"
        style={{ backgroundColor: accent }}
      />
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/50 pl-2">
        {label}
      </div>
      <div
        className={`text-2xl font-mono font-bold tabular-nums mt-1 pl-2 ${
          loading ? "opacity-30" : ""
        }`}
        style={{ color: highlight ? accent : "#fff" }}
      >
        {loading ? (
          <ArrowClockwise size={20} weight="bold" className="animate-spin" />
        ) : (
          value
        )}
      </div>
      {hint && !loading && (
        <div className="text-[9px] font-mono text-white/40 pl-2 mt-1 truncate">
          {hint}
        </div>
      )}
    </div>
  );
}
